import layerService from '@/api/layerService'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { MODULE_NAME, ACTIONS, MUTATIONS } from '@/store/locations/constants'
import { MODULE_NAME as MODULE_NAME_MAP, MUTATIONS as MUTATIONS_MAP } from '@/store/map/constants'
import { MODULE_NAME as MODULE_NAME_FILTER, GETTERS as GETTERS_FILTER, MUTATIONS as MUTATIONS_FILTER } from '@/store/filters/constants'
import {
  advertIconUrl,
  advertSquareIconUrl,
  shopIconUrl,
  shopIconOutlineUrl,
  ADVERT_TYPES
} from '@/utils/mapUtils'
import AdvertsClusterLayer from '@/api/AdvertClusterLayer'
import ShopsClusterLayer from '@/api/ShopClusterLayer'
import getMostFrequent from '@/utils/getMostFrequent'
import debounce from '@/utils/debounce'
import { subscribe } from '@/utils/vuex'
import { initialViewState } from '@/store/map/state'
import i18n from '@/i18n'

window.layerService = layerService

export default {
  name: 'Map',
  mixins: [],
  components: {
    MapComponent: () => import(/* webpackChunkName: "MapComponent" */ '@/components/map-component/MapComponent.vue'),
    MapSidebar: () => import(/* webpackChunkName: "MapSidebar" */ '@/components/map-sidebar/MapSidebar.vue'),
    Legend: () => import(/* webpackChunkName: "Legend" */ '@/components/legend/Legend.vue')
  },
  props: {},
  data () {
    return {
      sidebarExpanded: false,
      mapLoaded: false,
      shopsLayerId: 'shop_points',
      advertsLayerId: 'advert_points'
    }
  },
  computed: {
    ...mapState(MODULE_NAME, ['viewportFilter', 'shops', 'adverts', 'groups']),
    ...mapState(MODULE_NAME_MAP, ['viewState']),
    ...mapGetters(MODULE_NAME_FILTER, {
      filters: GETTERS_FILTER.GET_ROUTE_FILTERS
    }),
    ...mapGetters(MODULE_NAME_FILTER, {
      localPoisFilter: GETTERS_FILTER.GET_POIS_FILTERS,
      localAdvertsFilter: GETTERS_FILTER.GET_ADVERTS_FILTERS,
      localShopsFilter: GETTERS_FILTER.GET_SHOPS_FILTERS
    }),
    useViewportFilter () {
      return this.viewportFilter.enabled
    },
    mapExpanded () {
      return !this.mapLoaded || !this.sidebarExpanded
    },
    centerid () {
      const id = this.$route.params.centerid
      return id === 'new' ? null : id
    }
  },
  watch: {
    $route () {
      if (this.$route.path === '/map') {
        const viewState = initialViewState()
        this[MUTATIONS_MAP.FLY_TO]({
          longitude: viewState.longitude,
          latitude: viewState.latitude,
          zoom: viewState.zoom
        })
      }
    },
    filters: function () {
      this.onFiltersUpdate()
    },
    localPoisFilter: function () {
      this.onLocalFiltersUpdate('pois')
    },
    localAdvertsFilter: function () {
      this.onLocalFiltersUpdate('adverts')
    },
    localShopsFilter: function () {
      this.onLocalFiltersUpdate('shops')
    },
    useViewportFilter (flag) {
      if (flag) {
        this.updateViewportData()
      }
    },
    centerid () {
      this.updateCenterFilter()
    }
  },
  mounted () {
    this.sidebarExpanded = this.$route.name !== 'locations-list'
    this.debouncedUpdateViewportData = debounce(this.updateViewportData, 500)
    this.unsubscribe = subscribe(MODULE_NAME_MAP, MUTATIONS_MAP.SET_VIEWSTATE, () => {
      if (this.useViewportFilter) {
        this.debouncedUpdateViewportData()
      }
    })
    this.onMapLoaded()
  },
  beforeDestroy () {
    this[MUTATIONS.CLEAR_LOCATIONS]()
    this[MUTATIONS_FILTER.CLEAR_TEXT_FILTERS]()
    // layerService.removeLayer(this.shopsLayerId)
    layerService.removeLayer(this.advertsLayerId)
    if (this.unsubscribe) {
      this.unsubscribe()
    }
  },
  methods: {
    ...mapActions(MODULE_NAME, Object.values(ACTIONS)),
    ...mapMutations(MODULE_NAME, [
      MUTATIONS.SET_VIEWPORT_DATA,
      MUTATIONS.CLEAR_LOCATIONS
    ]),
    ...mapMutations(MODULE_NAME_MAP, [MUTATIONS_MAP.FLY_TO]),
    ...mapMutations(MODULE_NAME_FILTER, [ MUTATIONS_FILTER.SET_SHOPS_FILTER_VALUE, MUTATIONS_FILTER.SET_ADVERTS_FILTER_VALUE, MUTATIONS_FILTER.SET_POIS_FILTER_VALUE, MUTATIONS_FILTER.CLEAR_TEXT_FILTERS ]),
    resize () {
      this.sidebarExpanded = !this.sidebarExpanded
    },
    updateViewportData () {
      this[MUTATIONS.SET_VIEWPORT_DATA]({
        adverts: this.getViewportAdverts(),
        shops: this.getViewportShops()
      })
    },
    updateCenterFilter () {
      layerService.updateLayer(this.shopsLayerId, { data: this.getLayerData(this.shopsLayerId) })
      layerService.updateLayer(this.advertsLayerId, { data: this.getLayerData(this.advertsLayerId) })
    },
    getLayerData (layerId) {
      const shopid = Number(this.centerid)
      if (layerId === this.advertsLayerId) {
        return this.centerid
          ? this.adverts.data.filter(adv => adv.shop_related.id === shopid)
          : this.adverts.data
      }
      if (layerId === this.shopsLayerId) {
        const shops = this.shops.data.map(s => ({ id: Number(s.value), name: s.label, lat: s.lat, lng: s.lng }))
        return this.shopid
          ? shops.filter(s => s.id === shopid)
          : shops
      }
      // at this point in code layerId is not matched to any of the possible options so show an error for that
      // eslint-disable-next-line
      console.error(`[map.js] invalid layerId passed to getLayerData. Valid options are "${this.advertsLayerId}" and "${this.shopsLayerId}"`)
    },
    async onFiltersUpdate () {
      // TODO: uncoment to globar input search
      // if (!this.mapLoaded) {
      //   return
      // }
      if (this.localShopsFilter === '') {
        this[MUTATIONS.CLEAR_LOCATIONS]()
        // TODO: uncoment if don't want to persist tab filters
        // this[MUTATIONS_FILTER.CLEAR_TEXT_FILTERS]()
        await this.fetchLocations()
        this.updateShopsLayer()
      }
      // TODO: uncoment to mount adverts layer
      // this.updateAdvertsLayer()
    },
    async onLocalFiltersUpdate (type) {
      if (!this.mapLoaded) {
        return
      }

      if (type === 'pois') {
        await this[ACTIONS.FETCH_POIS]({ reset: true, page_size: 50, q: this.localPoisFilter, ordering: 'name' })
      } else if (type === 'adverts') {
        await this[ACTIONS.FETCH_ADVERTS]({ reset: true, page_size: 50, q: this.localAdvertsFilter, ordering: 'advert_id,address' })
      } else if (type === 'shops') {
        await this[ACTIONS.FETCH_SHOPS]({ reset: true, page_size: 50, q: this.localShopsFilter, ordering: 'name' })
        this.updateShopsLayer()
      }
    },
    async fetchLocations () {
      try {
        return Promise.all([
          this[ACTIONS.FETCH_SHOPS]({ reset: true, page_size: 1000, summary: 1, ordering: 'name' }),
          this[ACTIONS.FETCH_ADVERTS]({ reset: true, page_size: 50, ordering: 'advert_id,address' }),
          this[ACTIONS.FETCH_POIS]({ reset: true, page_size: 50, ordering: 'name' })
        ])
      } catch (err) {
        // eslint-disable-next-line
        console.error('[map.js] Error fetching locations', err)
      }
    },
    async onMapLoaded () {
      await this.fetchLocations()
      this.createLayers()
      this.mapLoaded = true
    },
    createLayers () {
      // this.createAdvertsLayer()
      this.createShopsLayer()
    },
    createAdvertsLayer () {
      layerService.addLayer({
        id: this.advertsLayerId,
        data: this.getLayerData(this.advertsLayerId),
        layerType: AdvertsClusterLayer,
        getPath: d => {
          return [
            [d.properties.shop_related.lng, d.properties.shop_related.lat],
            [...d.geometry.coordinates]
          ]
        },
        sizeScale: 32,
        textSizeScale: 20,
        pickable: true,
        getPosition: d => [d.lng, d.lat],
        getIcon: d => ({
          width: 40,
          height: 40,
          url: getMostFrequent(d.properties.advert_type.split('-')) === ADVERT_TYPES.PRODUCT
            ? advertIconUrl
            : advertSquareIconUrl
        }),
        getTooltip ({ object }) {
          const count = object.properties.point_count
          const label = object.properties.address
          const type = getMostFrequent(object.properties.advert_type.split('-'))
          const text = count > 1 ? `${count} ${i18n.t('ADVERTS')}` : label
          const html = `
            <div>
              <p>
                <small style="font-size: 80%; font-weight: 600">${type}</small>
              </p>
              <p>${text}</p>
            </div>
          `
          const style = {
            'background': 'var(--colorNegative)',
            color: 'var(--colorHigh)'
          }
          return { html, style, className: 'map-popup-white' }
        },
        onClick: ({ object, coordinate }) => {
          if (!object) {
            return
          }
          const { id, cluster } = object.properties
          if (cluster) {
            const [longitude, latitude] = coordinate
            this[MUTATIONS_MAP.FLY_TO]({
              longitude,
              latitude,
              zoom: this.viewState.zoom + 1
            })
          } else {
            this.sidebarExpanded = true
            this.$router.push({ name: 'advert-detail', params: { advertid: id } })
          }
        }
      })
    },
    createShopsLayer () {
      layerService.addLayer({
        id: this.shopsLayerId,
        data: this.getLayerData(this.shopsLayerId),
        layerType: ShopsClusterLayer,
        sizeScale: 46,
        textSizeScale: 24,
        pickable: true,
        getPosition: d => [d.lng, d.lat],
        getIcon: d => ({
          width: 40,
          height: 40,
          url: d.properties.cluster ? shopIconOutlineUrl : shopIconUrl
        }),
        onClick: ({ object, coordinate }) => {
          if (!object) {
            return
          }
          const cluster = object.properties.cluster
          const id = object.properties.id || object.properties.value
          if (cluster) {
            const [longitude, latitude] = coordinate
            this[MUTATIONS_MAP.FLY_TO]({
              longitude,
              latitude,
              zoom: this.viewState.zoom + 1
            })
          } else {
            this.sidebarExpanded = true
            this.$router.push({ name: 'center-detail', params: { centerid: id } })
          }
        },
        getTooltip ({ object }) {
          const count = object.properties.point_count
          const label = object.properties.name || object.properties.label
          const text = count > 1 ? `${count} ${i18n.t('CENTERS')}` : label
          const style = {
            'background': 'var(--colorNegative)',
            color: 'var(--colorHigh)'
          }
          return { text, style, className: 'map-popup-white' }
        }
      })
    },
    updateAdvertsLayer () {
      layerService.updateLayer(
        this.advertsLayerId,
        { data: this.adverts.data }
      )
    },
    updateShopsLayer () {
      layerService.updateLayer(
        this.shopsLayerId,
        { data: this.shops.data }
      )
    },
    getViewportAdverts () {
      return layerService.getViewportFeaturesGeoJSON(this.advertsLayerId)
    },
    getViewportShops () {
      return layerService.getViewportFeaturesGeoJSON(this.shopsLayerId)
    }
  }
}
